import { Radio } from 'antd5';
import i18n from 'i18next';
import { get } from 'lodash';
import { Link } from 'react-router-dom';

import { StatusTag } from 'components/StatusTag';
import {
  TURBINE_EDIT_MAKE,
  TURBINE_EDIT_MODEL,
  TURBINE_EDIT_STATUS,
} from 'utils/access-control/rules';
import { standardInput } from 'utils/editable';

import { defaultNone, getFilterDropdown, getOnFilter, None, sortBy } from './helpers';

const NONE = <None />;

export const name = {
  key: 'name',
  title: i18n.t('table.turbine', 'Turbine'),
  render(record) {
    const { id, name } = record || {};
    return [id, name].every(Boolean) ? <Link to={`/turbines/${id}`}>{name}</Link> : NONE;
  },
  sorter: sortBy('name'),
  get: record => get(record, 'name'),
  filterProperty: 'name',
};

export const serial = {
  key: 'serial',
  title: i18n.t('table.serialNo', 'Serial No.'),
  dataIndex: 'serial',
  filterProperty: 'serial',
  sorter: sortBy('serial'),
  get: record => get(record, 'serial'),
  render: (serial, record) => {
    const { id } = record || {};
    return <Link to={`/turbines/${id}`}>{serial ?? i18n.t('table.noSerial', '(No Serial)')}</Link>;
  },
};

const statusFilterConfig = {
  key: 'status',
  type: 'string',
  options: ['ACTIVE', 'INACTIVE'],
  exactMatch: true,
};

export const status = {
  key: 'status',
  title: i18n.t('table.status', 'Status'),
  dataIndex: 'status',
  render: status => (status ? <StatusTag value={status} /> : NONE),
  sorter: sortBy('status'),
  get: record => get(record, 'status'),
  filters: [
    { text: i18n.t('table.online', 'Online'), value: 'ACTIVE' },
    { text: i18n.t('table.offline', 'Offline'), value: 'INACTIVE' },
  ],
  onFilter: getOnFilter(statusFilterConfig),
  filterDropdown: getFilterDropdown(statusFilterConfig),
  editRule: TURBINE_EDIT_STATUS,
  edit(record, { onChange, ...props }) {
    if (!record) {
      return <None message={i18n.t('common.unableToEdit', 'Unable to edit')} />;
    }
    const { status } = record;
    return (
      <Radio.Group
        defaultValue={status}
        onChange={
          onChange &&
          (e =>
            onChange({
              status: e.target.value,
            }))
        }
        {...props}
      >
        <Radio value="ACTIVE">{i18n.t('table.online', 'Online')}</Radio>
        <Radio value="INACTIVE">{i18n.t('table.offline', 'Offline')}</Radio>
      </Radio.Group>
    );
  },
};

export const make = {
  key: 'make',
  title: i18n.t('table.make', 'Make'),
  dataIndex: 'make',
  render: defaultNone(),
  sorter: sortBy('make'),
  get: record => get(record, 'make'),
  filterProperty: 'make',
  onFilter: getOnFilter({ key: 'make', type: 'string' }),
  editRule: TURBINE_EDIT_MAKE,
  edit: standardInput('make'),
};

export const model = {
  key: 'model',
  title: i18n.t('table.model', 'Model'),
  dataIndex: 'model',
  render: defaultNone(),
  sorter: sortBy('model'),
  get: record => get(record, 'model'),
  filterProperty: 'model',
  onFilter: getOnFilter({ key: 'model', type: 'string' }),
  editRule: TURBINE_EDIT_MODEL,
  edit: standardInput('model'),
};

export const location = {
  key: 'location.name',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'location',
  render: location => {
    const { id, name } = location || {};
    return [id, name].every(Boolean) ? <Link to={`/sites/${id}`}>{name}</Link> : NONE;
  },
  sorter: sortBy('location.name'),
  get: record => get(record, ['location', 'name']),
  filterProperty: 'location.name',
};

export const atlasLocation = {
  key: 'parent.name',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'parent',
  render: parent => {
    const { id, name } = parent || {};
    return [id, name].every(Boolean) ? <Link to={`/sites/${id}`}>{name}</Link> : NONE;
  },
  sorter: sortBy('parent.name'),
  get: record => get(record, ['parent']),
  filterProperty: 'parent.name',
};

export const inspections = {
  key: 'inspections.length',
  title: i18n.t('table.inspections', 'Inspections'),
  dataIndex: 'inspections.length',
  render(count, turbine) {
    const { id } = turbine || {};
    return count ? <Link to={`/turbines/${id}/inspections`}>{count}</Link> : NONE;
  },
};
